import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Build as BuildIcon,
  CheckCircle,
  Schedule,
  Cancel,
  Warning,
  LocationOn,
  AccessTime,
} from '@mui/icons-material';
import SharedCalendar from '../../components/SharedCalendar/SharedCalendar';
import ScheduleForm from '../../components/ScheduleForm/ScheduleForm';

interface Installation {
  id: string;
  projectId: string;
  projectName: string;
  customer: string;
  customerId: string;
  status: 'new_install' | 'scheduled' | 'in_progress' | 'completed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  scheduledDate: string;
  startTime: string;
  endTime: string;
  estimatedDuration: number; // in hours
  actualDuration: number; // in hours
  location: string;
  installer: string;
  team: string[];
  equipment: string[];
  notes: string;
  weatherDependent: boolean;
  specialRequirements: string[];
  createdAt: string;
  updatedAt: string;
}

const Installations: React.FC = () => {
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInstallation, setSelectedInstallation] = useState<Installation | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sharedCalendarOpen, setSharedCalendarOpen] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [scheduleFormOpen, setScheduleFormOpen] = useState(false);
  const [selectedInstallationForSchedule, setSelectedInstallationForSchedule] = useState<Installation | null>(null);
  const navigate = useNavigate();

  const fetchInstallations = useCallback(async () => {
    try {
      setLoading(true);
      // Mock data for now - replace with actual API call
      const mockInstallations: Installation[] = [
        {
          id: '1',
          projectId: '1',
          projectName: 'Luxury Home LED Installation',
          customer: 'John & Sarah Johnson',
          customerId: '1',
          status: 'in_progress',
          priority: 'high',
          scheduledDate: '2024-10-20',
          startTime: '08:00',
          endTime: '17:00',
          estimatedDuration: 8,
          actualDuration: 4,
          location: '123 Oak Street, Beverly Hills, CA',
          installer: 'Mike Rodriguez',
          team: ['Mike Rodriguez', 'Alex Chen', 'Sarah Wilson'],
          equipment: ['LED Strips', 'Dimmer Switches', 'Control Panel', 'Wire'],
          notes: 'Customer prefers warm white lighting in living areas',
          weatherDependent: false,
          specialRequirements: ['Lift equipment needed for high ceilings'],
          createdAt: '2024-10-15T10:00:00Z',
          updatedAt: '2024-10-20T12:30:00Z'
        },
        {
          id: '2',
          projectId: '2',
          projectName: 'Commercial Office Building',
          customer: 'Metro Properties LLC',
          customerId: '2',
          status: 'scheduled',
          priority: 'medium',
          scheduledDate: '2024-11-05',
          startTime: '07:00',
          endTime: '18:00',
          estimatedDuration: 10,
          actualDuration: 0,
          location: '456 Business Ave, Downtown, CA',
          installer: 'Lisa Thompson',
          team: ['Lisa Thompson', 'David Park', 'Maria Garcia', 'Tom Wilson'],
          equipment: ['Smart LED Panels', 'Motion Sensors', 'Central Controller', 'Cables'],
          notes: 'Weekend installation to avoid business disruption',
          weatherDependent: false,
          specialRequirements: ['Security clearance required', 'After hours access'],
          createdAt: '2024-10-10T09:00:00Z',
          updatedAt: '2024-10-16T16:45:00Z'
        },
        {
          id: '3',
          projectId: '3',
          projectName: 'Restaurant Ambiance Lighting',
          customer: 'Bella Vista Restaurant',
          customerId: '3',
          status: 'completed',
          priority: 'medium',
          scheduledDate: '2024-09-15',
          startTime: '09:00',
          endTime: '16:00',
          estimatedDuration: 7,
          actualDuration: 6.5,
          location: '789 Main Street, Santa Monica, CA',
          installer: 'Alex Chen',
          team: ['Alex Chen', 'Sarah Wilson'],
          equipment: ['LED Strips', 'Dimmable Fixtures', 'Color Controllers'],
          notes: 'Installation completed ahead of schedule',
          weatherDependent: false,
          specialRequirements: ['Work during restaurant closed hours'],
          createdAt: '2024-09-01T11:00:00Z',
          updatedAt: '2024-09-15T16:30:00Z'
        },
        {
          id: '4',
          projectId: '4',
          projectName: 'Outdoor Landscape Lighting',
          customer: 'Robert & Emily Davis',
          customerId: '4',
          status: 'new_install',
          priority: 'low',
          scheduledDate: '2024-10-25',
          startTime: '10:00',
          endTime: '15:00',
          estimatedDuration: 5,
          actualDuration: 0,
          location: '321 Garden Lane, Malibu, CA',
          installer: 'Mike Rodriguez',
          team: ['Mike Rodriguez'],
          equipment: ['Weatherproof LEDs', 'Solar Panels', 'Motion Detectors'],
          notes: 'New customer installation',
          weatherDependent: true,
          specialRequirements: ['Weather dependent - reschedule if rain'],
          createdAt: '2024-10-05T14:00:00Z',
          updatedAt: '2024-10-18T10:15:00Z'
        },
        {
          id: '5',
          projectId: '5',
          projectName: 'Retail Store Display Lighting',
          customer: 'Fashion Forward Boutique',
          customerId: '5',
          status: 'scheduled',
          priority: 'high',
          scheduledDate: '2024-10-22',
          startTime: '08:30',
          endTime: '14:30',
          estimatedDuration: 6,
          actualDuration: 0,
          location: '555 Shopping Center, West Hollywood, CA',
          installer: 'Sarah Wilson',
          team: ['Sarah Wilson', 'Alex Chen'],
          equipment: ['Track Lighting', 'Spotlights', 'Display LEDs'],
          notes: 'Scheduled for next week',
          weatherDependent: false,
          specialRequirements: ['Store closed during installation'],
          createdAt: '2024-10-12T13:00:00Z',
          updatedAt: '2024-10-19T09:20:00Z'
        },
        {
          id: '6',
          projectId: '6',
          projectName: 'Smart Home Integration',
          customer: 'Tech Savvy Family',
          customerId: '6',
          status: 'completed',
          priority: 'high',
          scheduledDate: '2024-09-30',
          startTime: '09:00',
          endTime: '17:00',
          estimatedDuration: 8,
          actualDuration: 7.5,
          location: '888 Innovation Drive, Silicon Valley, CA',
          installer: 'David Park',
          team: ['David Park', 'Maria Garcia'],
          equipment: ['Smart LEDs', 'Home Automation Hub', 'Voice Controls'],
          notes: 'Completed successfully with customer training',
          weatherDependent: false,
          specialRequirements: ['Customer training session included'],
          createdAt: '2024-09-15T10:00:00Z',
          updatedAt: '2024-09-30T17:00:00Z'
        }
      ];
      
      setInstallations(mockInstallations);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch installations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInstallations();
  }, [fetchInstallations]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, installation: Installation) => {
    setAnchorEl(event.currentTarget);
    setSelectedInstallation(installation);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInstallation(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (selectedInstallation) {
      try {
        // Mock delete - replace with actual API call
        setInstallations(installations.filter(i => i.id !== selectedInstallation.id));
        setDeleteDialogOpen(false);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete installation');
      }
    }
  };

  const handleAddInstallation = () => {
    setSharedCalendarOpen(true);
  };

  const handleCalendarDateSelect = (date: Date) => {
    setCalendarDate(date);
    setSharedCalendarOpen(false);
    // Here you could navigate to a form or open a dialog to add a new installation
    console.log('Selected date for new installation:', date);
  };

  const handleSharedCalendarAdd = (date: Date) => {
    // Here you could open a form to add a new installation
    console.log('Add new installation for:', date);
    // Refresh the installations after adding
    fetchInstallations();
  };

  const handleNewInstallClick = (installation: Installation) => {
    setSelectedInstallationForSchedule(installation);
    setScheduleFormOpen(true);
  };

  const handleScheduleFormClose = () => {
    setScheduleFormOpen(false);
    setSelectedInstallationForSchedule(null);
  };

  const handleScheduleCreated = (schedule: any) => {
    // Update the installation status from 'new_install' to 'scheduled'
    setInstallations(prev => prev.map(inst => 
      inst.id === selectedInstallationForSchedule?.id 
        ? { ...inst, status: 'scheduled' as const }
        : inst
    ));
    setScheduleFormOpen(false);
    setSelectedInstallationForSchedule(null);
  };

  const handleEditClick = () => {
    // Navigate to edit installation
    handleMenuClose();
  };

  const handleViewClick = () => {
    // Navigate to installation details
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'scheduled': return 'info';
      case 'new_install': return 'primary';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'in_progress': return <BuildIcon />;
      case 'scheduled': return <Schedule />;
      case 'new_install': return <Add />;
      default: return <BuildIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const filteredInstallations = installations.filter(installation => {
    const matchesSearch = installation.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         installation.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         installation.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || installation.status === statusFilter;
    const matchesPriority = !priorityFilter || installation.priority === priorityFilter;
    const matchesDate = !dateFilter || installation.scheduledDate === dateFilter;
    
    return matchesSearch && matchesStatus && matchesPriority && matchesDate;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#D4AF37' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh', p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography 
          variant="h4"
          sx={{
            color: '#D4AF37',
            fontWeight: 700,
          }}
        >
          🔧 Installs
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddInstallation}
          sx={{
            background: 'linear-gradient(45deg, #D4AF37 30%, #F57F17 90%)',
            color: '#000000',
            '&:hover': {
              background: 'linear-gradient(45deg, #F57F17 30%, #D4AF37 90%)',
            },
          }}
        >
          Schedule Installation
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, backgroundColor: '#2D1B1B', color: '#FF6B6B', border: '1px solid #FF6B6B' }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)',
            border: '1px solid #333333',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>New Install</Typography>
                  <Typography variant="h4" sx={{ color: '#2196F3', fontWeight: 700 }}>
                    {installations.filter(i => i.status === 'new_install').length}
                  </Typography>
                </Box>
                <Add sx={{ color: '#2196F3', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)',
            border: '1px solid #333333',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>Scheduled</Typography>
                  <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 700 }}>
                    {installations.filter(i => i.status === 'scheduled').length}
                  </Typography>
                </Box>
                <Schedule sx={{ color: '#4CAF50', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)',
            border: '1px solid #333333',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>In Progress</Typography>
                  <Typography variant="h4" sx={{ color: '#FF9800', fontWeight: 700 }}>
                    {installations.filter(i => i.status === 'in_progress').length}
                  </Typography>
                </Box>
                <BuildIcon sx={{ color: '#FF9800', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Card sx={{ mb: 3, background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)', border: '1px solid #333333' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search installations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#D4AF37' }} />
                    </InputAdornment>
                  ),
                  sx: { color: '#FFFFFF' }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#B0B0B0' },
                    '&:hover fieldset': { borderColor: '#FFFFFF' },
                    '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                  },
                  '& .MuiInputLabel-root': { color: '#B0B0B0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#B0B0B0' },
                  '&:hover fieldset': { borderColor: '#FFFFFF' },
                  '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                },
                '& .MuiInputLabel-root': { color: '#B0B0B0' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
              }}>
                <InputLabel sx={{ color: '#B0B0B0' }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value as string)}
                  sx={{ color: '#FFFFFF', '& .MuiSelect-icon': { color: '#D4AF37' } }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="new_install">New Install</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#B0B0B0' },
                  '&:hover fieldset': { borderColor: '#FFFFFF' },
                  '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                },
                '& .MuiInputLabel-root': { color: '#B0B0B0' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
              }}>
                <InputLabel sx={{ color: '#B0B0B0' }}>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => setPriorityFilter(e.target.value as string)}
                  sx={{ color: '#FFFFFF', '& .MuiSelect-icon': { color: '#D4AF37' } }}
                >
                  <MenuItem value="">All Priorities</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#B0B0B0' },
                    '&:hover fieldset': { borderColor: '#FFFFFF' },
                    '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                  },
                  '& .MuiInputLabel-root': { color: '#B0B0B0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setPriorityFilter('');
                  setDateFilter('');
                }}
                sx={{
                  color: '#D4AF37',
                  borderColor: '#D4AF37',
                  '&:hover': {
                    borderColor: '#F57F17',
                    color: '#F57F17',
                  },
                }}
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Categorized Installations */}
      <Grid container spacing={3}>
        {/* New Install */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)', 
            border: '1px solid #333333',
            height: '100%'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Add sx={{ color: '#2196F3', fontSize: 32 }} />
                <Typography variant="h5" sx={{ color: '#2196F3', fontWeight: 700 }}>
                  New Install ({installations.filter(i => i.status === 'new_install').length})
                </Typography>
              </Box>
              <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(33, 150, 243, 0.1)', borderRadius: 1, border: '1px solid rgba(33, 150, 243, 0.3)' }}>
                <Typography variant="body2" sx={{ color: '#2196F3', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule fontSize="small" />
                  These installations need to be scheduled
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {installations.filter(i => i.status === 'new_install').map((installation) => (
                  <Card key={installation.id} sx={{ 
                    mb: 2, 
                    backgroundColor: '#1A1A1A', 
                    border: '1px solid #333333',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.1)' }
                  }}
                  onClick={() => handleNewInstallClick(installation)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 1 }}>
                            {installation.projectName}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                            {installation.customer}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                            📍 {installation.location}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                            📅 {formatDate(installation.scheduledDate)} at {formatTime(installation.startTime)}
                          </Typography>
                          <Box display="flex" gap={1} mt={1}>
                            <Chip
                              label={installation.priority.toUpperCase()}
                              size="small"
                              color={getPriorityColor(installation.priority) as any}
                              sx={{ fontWeight: 600 }}
                            />
                            {installation.weatherDependent && (
                              <Chip 
                                label="Weather Dependent" 
                                size="small" 
                                sx={{ backgroundColor: '#FF9800', color: '#000000' }}
                              />
                            )}
                          </Box>
                        </Box>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClick(e, installation);
                          }}
                          sx={{ color: '#D4AF37' }}
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                {installations.filter(i => i.status === 'new_install').length === 0 && (
                  <Typography variant="body2" sx={{ color: '#AAAAAA', textAlign: 'center', py: 4 }}>
                    No new installations
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Scheduled */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)', 
            border: '1px solid #333333',
            height: '100%'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Schedule sx={{ color: '#4CAF50', fontSize: 32 }} />
                <Typography variant="h5" sx={{ color: '#4CAF50', fontWeight: 700 }}>
                  Scheduled ({installations.filter(i => i.status === 'scheduled').length})
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {installations.filter(i => i.status === 'scheduled').map((installation) => (
                  <Card key={installation.id} sx={{ 
                    mb: 2, 
                    backgroundColor: '#1A1A1A', 
                    border: '1px solid #333333',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' }
                  }}
                  onClick={() => navigate(`/installs/${installation.id}`)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 1 }}>
                            {installation.projectName}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                            {installation.customer}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                            📍 {installation.location}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                            📅 {formatDate(installation.scheduledDate)} at {formatTime(installation.startTime)}
                          </Typography>
                          <Box display="flex" gap={1} mt={1}>
                            <Chip
                              label={installation.priority.toUpperCase()}
                              size="small"
                              color={getPriorityColor(installation.priority) as any}
                              sx={{ fontWeight: 600 }}
                            />
                            {installation.weatherDependent && (
                              <Chip 
                                label="Weather Dependent" 
                                size="small" 
                                sx={{ backgroundColor: '#FF9800', color: '#000000' }}
                              />
                            )}
                          </Box>
                        </Box>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClick(e, installation);
                          }}
                          sx={{ color: '#D4AF37' }}
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                {installations.filter(i => i.status === 'scheduled').length === 0 && (
                  <Typography variant="body2" sx={{ color: '#AAAAAA', textAlign: 'center', py: 4 }}>
                    No scheduled installations
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* In Progress */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)', 
            border: '1px solid #333333',
            height: '100%'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <BuildIcon sx={{ color: '#FF9800', fontSize: 32 }} />
                <Typography variant="h5" sx={{ color: '#FF9800', fontWeight: 700 }}>
                  In Progress ({installations.filter(i => i.status === 'in_progress').length})
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {installations.filter(i => i.status === 'in_progress').map((installation) => (
                  <Card key={installation.id} sx={{ 
                    mb: 2, 
                    backgroundColor: '#1A1A1A', 
                    border: '1px solid #333333',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.1)' }
                  }}
                  onClick={() => navigate(`/installs/${installation.id}`)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 1 }}>
                            {installation.projectName}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                            {installation.customer}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                            📍 {installation.location}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                            📅 {formatDate(installation.scheduledDate)} at {formatTime(installation.startTime)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                            ⏱️ {installation.actualDuration}h / {installation.estimatedDuration}h
                          </Typography>
                          <Box display="flex" gap={1} mt={1}>
                            <Chip
                              label={installation.priority.toUpperCase()}
                              size="small"
                              color={getPriorityColor(installation.priority) as any}
                              sx={{ fontWeight: 600 }}
                            />
                            {installation.weatherDependent && (
                              <Chip 
                                label="Weather Dependent" 
                                size="small" 
                                sx={{ backgroundColor: '#FF9800', color: '#000000' }}
                              />
                            )}
                          </Box>
                        </Box>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClick(e, installation);
                          }}
                          sx={{ color: '#D4AF37' }}
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                {installations.filter(i => i.status === 'in_progress').length === 0 && (
                  <Typography variant="body2" sx={{ color: '#AAAAAA', textAlign: 'center', py: 4 }}>
                    No installations in progress
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: '#1A1A1A',
            border: '1px solid #333333',
          },
        }}
      >
        <MenuItem onClick={handleViewClick} sx={{ color: '#FFFFFF', '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' } }}>
          <ListItemIcon>
            <Visibility fontSize="small" sx={{ color: '#D4AF37' }} />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditClick} sx={{ color: '#FFFFFF', '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' } }}>
          <ListItemIcon>
            <Edit fontSize="small" sx={{ color: '#D4AF37' }} />
          </ListItemIcon>
          <ListItemText>Edit Installation</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: '#F44336', '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' } }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: '#F44336' }} />
          </ListItemIcon>
          <ListItemText>Delete Installation</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1A1A1A',
            border: '1px solid #333333',
            color: '#FFFFFF',
          }
        }}
      >
        <DialogTitle sx={{ color: '#D4AF37' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#FFFFFF' }}>
            Are you sure you want to delete this installation? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            sx={{ color: '#FFFFFF', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            autoFocus 
            sx={{ color: '#F44336', '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Form for New Installs */}
      {selectedInstallationForSchedule && (
        <ScheduleForm
          open={scheduleFormOpen}
          onClose={handleScheduleFormClose}
          selectedDate={new Date(selectedInstallationForSchedule.scheduledDate)}
          onScheduleCreated={handleScheduleCreated}
          prefillData={{
            customerId: selectedInstallationForSchedule.customerId,
            customer: selectedInstallationForSchedule.customer,
            description: selectedInstallationForSchedule.notes,
            priority: selectedInstallationForSchedule.priority,
            notes: selectedInstallationForSchedule.notes,
            type: 'installation'
          }}
        />
      )}

      {/* Shared Calendar Modal */}
      <SharedCalendar
        open={sharedCalendarOpen}
        onClose={() => setSharedCalendarOpen(false)}
        onDateSelect={handleCalendarDateSelect}
        onAddSchedule={handleSharedCalendarAdd}
        selectedDate={calendarDate}
        title="Select Date for New Installation"
      />
    </Box>
  );
};

export default Installations;
